import { FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Button } from '../../components/UI';
import { Input, InputCode } from '../../components/form';

import {
  confirmPasswordValidator,
  passwordValidator,
} from '../../components/form/Input/validators/password-validator';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../../context/Auth';
import { emailValidator } from '../../components/form/Input/validators/email-validator';
import { useSession } from '../../context/Session';
import { Icon } from '../../components/UI';
import { Modal } from '../../components/Modal';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const { forgotPassword, forgotPasswordSubmit } = useAuth();

  const { getSessionItem } = useSession();

  const [linkSent, setLinkSet] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [resErr, setResErr] = useState(null as any);

  const location = useLocation();

  const email = (location.state as any)?.email;

  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      email: getSessionItem('email'),
      code: '',
      password: '',
      confirmPassword: '',
    },
    onSubmit: () => {
      setIsLoading(true);

      if (!linkSent) {
        forgotPassword(formik.values.email)
          .then((res) => {
            setLinkSet(true);
            setIsLoading(false);
          })
          .catch((err) => {
            setResErr(
              t(`forgotPassword.form.error.${err.code}`, {
                defaultValue: err.message,
              })
            );
            setIsLoading(false);
          });
      } else {
        forgotPasswordSubmit(
          formik.values.email,
          formik.values.code,
          formik.values.password
        )
          .then((res) => {
            setPasswordChanged(true);
            setIsLoading(false);
          })
          .catch((err) => {
            setIsLoading(false);
            setResErr(
              t(`forgotPassword.form.error.${err.code}`, {
                defaultValue: err.message,
              })
            );
          });
      }
    },
    validateOnMount: true,
  });

  const forgotPasswordElement = () => (
    <Modal
      isOpen={true}
      backdrop={'solid'}
      handleMerlinLogo={true}
      isClosable={false}
      padding={'huge'}
    >
      <>
        <div className={'text-center'}>
          <h1
            className={'text-black text-xl font-semibold mb-4 dark:text-white'}
          >
            {email ? t('forgotPassword.title2') : t('forgotPassword.title')}
          </h1>
          <p className={'text-grey-50 text-sm whitespace-pre-wrap'}>
            {t('forgotPassword.subtitle')}
          </p>

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              <div className="mt-12 flex flex-col justify-center items-center">
                <Input
                  name="email"
                  formik={formik}
                  id="email"
                  size="medium"
                  validate={emailValidator}
                  type="text"
                  label={t('forgotPassword.form.label.email')}
                  placeholder={t('forgotPassword.form.placeholder.email')}
                />
                <div className={'text-negative text-xs'}>
                  {formik.errors.email && formik.touched.email
                    ? t('forgotPassword.form.error.email')
                    : resErr || ''}
                </div>
              </div>
              <div className="mt-10">
                <Button type="submit" variant="primary" buttonClass="w-64">
                  {t('forgotPassword.form.send')}
                </Button>
              </div>
            </form>
          </FormikProvider>
          <div className="mt-5">
            <button
              className="text-sm text-black font-semibold dark:text-white"
              onClick={() => {
                navigate('/signin');
              }}
            >
              {t('forgotPassword.form.back')}
            </button>
          </div>
        </div>
      </>
    </Modal>
  );

  const createNewPasswordElement = () => {
    return (
      <Modal
        isOpen={true}
        backdrop={'solid'}
        handleMerlinLogo={true}
        isClosable={false}
        padding={'base'}
      >
        <div
          className={
            'flex flex-col justify-center px-32 text-center items-center h-full'
          }
        >
          <h1
            className={
              'text-black dark:text-white text-xl font-semibold mb-4 dark:text-white'
            }
          >
            {t('forgotPassword.confirmResetPassword')}
          </h1>
          <p className={'text-grey-50 text-sm whitespace-pre-wrap'}>
            <Trans
              i18nKey="forgotPassword.enterCode"
              values={{ email: formik.values.email }}
            >
              enter code for
              <span className="text-black dark:text-white font-semibold">
                {formik.values.email}
              </span>
              to reset
            </Trans>
          </p>
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              <div className="mt-12">
                <InputCode
                  name="code"
                  id="code"
                  numberOfDigits={6}
                  formik={formik}
                  label={t('forgotPassword.form.label.code')}
                />
              </div>
              <div className="mt-10">
                <Input
                  name="password"
                  formik={formik}
                  id="password"
                  showPasswordHint={true}
                  validate={passwordValidator}
                  size="medium"
                  type="password"
                  label={t('forgotPassword.form.label.password')}
                  placeholder={t('forgotPassword.form.placeholder.password')}
                />
              </div>
              <div className="mt-10">
                <Input
                  name="confirmPassword"
                  formik={formik}
                  validate={(value: string) =>
                    confirmPasswordValidator(value, formik.values.password)
                  }
                  id="confirmPassword"
                  size="medium"
                  type="password"
                  label={t('forgotPassword.form.label.confirmPassword')}
                  placeholder={t(
                    'forgotPassword.form.placeholder.confirmPassword'
                  )}
                  errorMessage={
                    formik.errors.confirmPassword &&
                    formik.touched.confirmPassword
                      ? t('forgotPassword.form.error.confirmPassword')
                      : ''
                  }
                />
              </div>
              <div className="mt-10">
                <Button
                  type="submit"
                  variant="primary"
                  buttonClass="w-64"
                  loading={isLoading}
                >
                  {t('forgotPassword.form.submit')}
                </Button>
              </div>
            </form>
          </FormikProvider>
        </div>
      </Modal>
    );
  };
  const passwordChangedElement = () => (
    <Modal
      isOpen={true}
      backdrop={'solid'}
      handleMerlinLogo={true}
      isClosable={false}
      padding={'huge'}
    >
      <div className={'text-center'}>
        <div className={'text-center mb-4'}>
          <Icon
            svgName={'circleCheck'}
            className="w-32 h-32 mb-8 mx-auto"
            fill={false}
          />
        </div>
        <h1 className={'text-black text-xl font-semibold mb-4 dark:text-white'}>
          {t('forgotPassword.passwordChanged')}
        </h1>
        <p className={'text-grey-50 text-sm whitespace-pre-wrap'}>
          {t('forgotPassword.passwordChangedSubtitle')}
        </p>

        <div className="mt-10">
          <Button
            type="button"
            variant="primary"
            onClick={() => navigate('/signin')}
            buttonClass="w-64"
          >
            {t('forgotPassword.form.back')}
          </Button>
        </div>
      </div>
    </Modal>
  );

  return (
    <>
      {!linkSent && forgotPasswordElement()}
      {linkSent && !passwordChanged && createNewPasswordElement()}
      {passwordChanged && passwordChangedElement()}
    </>
  );
};

export default ForgotPassword;
